import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { Search, Truck, Building2, FileText, ReceiptText } from "lucide-react";

export function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then((r) => { setRes(r.data); setOpen(true); }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); setRes(null); navigate(path); };

  const empty = res && !res.vehicles.length && !res.vendors.length && !res.cash_requests.length && !res.vendor_bills.length;

  const Group = ({ icon: Icon, label, children }) => (
    <div className="py-1">
      <p className="px-3 py-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Icon className="h-3 w-3" />{label}</p>
      {children}
    </div>
  );
  const Row = ({ testid, onClick, primary, secondary }) => (
    <button onClick={onClick} data-testid={testid}
      className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-secondary transition-colors">
      <span className="text-sm font-medium text-foreground truncate">{primary}</span>
      {secondary && <span className="text-[11px] text-muted-foreground shrink-0">{secondary}</span>}
    </button>
  );

  return (
    <div ref={ref} className="relative" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-100/70" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => res && setOpen(true)}
          placeholder="Cari kendaraan, vendor, transaksi..."
          data-testid="global-search-input"
          className="w-full h-9 pl-9 pr-3 rounded-lg bg-white/10 border border-white/20 text-sm text-white placeholder:text-emerald-100/60 focus:outline-none focus:bg-white/15 focus:border-white/40"
        />
      </div>
      {open && res && (
        <div className="absolute mt-2 w-full max-h-[70vh] overflow-y-auto rounded-xl border bg-white text-foreground shadow-xl z-50 divide-y" data-testid="global-search-results">
          {empty ? (
            <p className="px-3 py-4 text-sm text-muted-foreground text-center" data-testid="global-search-empty">Tidak ada hasil untuk "{q}"</p>
          ) : (
            <>
              {res.vehicles.length > 0 && (
                <Group icon={Truck} label="Kendaraan">
                  {res.vehicles.map((v) => (
                    <Row key={v.id} testid={`search-vehicle-${v.id}`} onClick={() => go(`/vehicles/${v.id}`)}
                      primary={`${v.plate_number} · ${v.vehicle_type}`} secondary={v.branch} />
                  ))}
                </Group>
              )}
              {res.vendors.length > 0 && (
                <Group icon={Building2} label="Vendor">
                  {res.vendors.map((v) => (
                    <Row key={v.id} testid={`search-vendor-${v.id}`} onClick={() => go("/vendor-bills")} primary={v.name} />
                  ))}
                </Group>
              )}
              {res.cash_requests.length > 0 && (
                <Group icon={FileText} label="Pengajuan Kas">
                  {res.cash_requests.map((r) => (
                    <Row key={r.id} testid={`search-request-${r.id}`} onClick={() => go(`/requests/${r.id}`)}
                      primary={`${r.requester_name} · ${r.reason}`} secondary={formatRupiah(r.amount)} />
                  ))}
                </Group>
              )}
              {res.vendor_bills.length > 0 && (
                <Group icon={ReceiptText} label="Tagihan Vendor">
                  {res.vendor_bills.map((b) => (
                    <Row key={b.id} testid={`search-bill-${b.id}`} onClick={() => go("/vendor-bills")}
                      primary={b.vendor_name} secondary={formatRupiah(b.amount)} />
                  ))}
                </Group>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
